export function addEvent<T extends Element | Window>(event: string, elem: T,
    listener: (this: T, event: Event) => void): void {
    event = event.toLowerCase();

    const modListener = evt => {
        evt = evt || window.event;
        listener.call(elem, evt);
    };

    if (elem.addEventListener) {
        // W3C DOM
        elem.addEventListener(event, modListener, false);
    } else {
        const ieElem = (elem as any) as {
            attachEvent(event: string, listener: EventListener): boolean
        };

        if (ieElem.attachEvent) {
            // IE DOM
            ieElem.attachEvent('on' + event, modListener);
        } else {
            // Add as attribute (can be overwritten)
            elem[event] = modListener;
        }
    }
}

export function getChildrenOfType<T extends HTMLElement>(element: Element, type: string): T[] {
    type = type.toLowerCase();
    return collectionToArray<T>(element.children)
        .filter(x => x.nodeName.toLowerCase() === type);
}

export function collectionToArray<T extends HTMLElement>(collection: HTMLCollection): T[] {
    return Array.prototype.slice.call(collection);
}

interface ICustomNode extends Node {
    id: string;
    dispatchEvent: (evt: Event, x?: boolean) => boolean;
    fireEvent?: (evtName: string, evt: Event) => void;
}

export function fireEvent(node: ICustomNode, eventName: string): void {

    // Make sure we use the ownerDocument from the provided node to avoid cross-window problems
    let doc;
    if (node.ownerDocument) {
        doc = node.ownerDocument;
    } else if (node.nodeType === 9) {
        // The node may be the document itself, nodeType 9 = DOCUMENT_NODE
        doc = node;
    } else {
        throw new Error('Invalid node passed to fireEvent: ' + node.id);
    }

    if (node.dispatchEvent) {
        // Gecko-style approach (now the standard) takes more work
        let eventClass = '';

        // Different events have different event classes.
        // If this switch statement can't map an eventName to an eventClass,
        // the event firing is going to fail.
        switch (eventName) {
            case 'click':
            case 'mousedown':
            case 'mouseup':
                eventClass = 'MouseEvents';
                break;

            case 'focus':
            case 'change':
            case 'blur':
            case 'select':
                eventClass = 'HTMLEvents';
                break;

            default:
                throw new Error(`fireEvent: Couldn't find an event class for event '${eventName}'.`);
        }

        const event = doc.createEvent(eventClass);
        event.initEvent(eventName, true, true); // All events created as bubbling and cancelable.
        event.synthetic = true; // Allow detection of synthetic events

        // The second parameter says go ahead with the default action
        node.dispatchEvent(event, true);
    } else if (node.fireEvent) {
        // IE-old school style
        const event = doc.createEventObject();
        event.synthetic = true; // Allow detection of synthetic events
        node.fireEvent('on' + eventName, event);
    }
}

export function eID<T extends HTMLElement>(id: string) {
    const el = document.getElementById(id);
    if (!el) throw new Error(`Element with ID '${id}' does not exist`);
    return el as T;
}

export function eClass<T extends HTMLElement>(className: string): T[] {
    const els: T[] = collectionToArray(document.getElementsByClassName(className));
    if (els.length === 0) {
        throw new Error(`No elements with class '${className}' exist`);
    }
    return els;
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/assets/scripts/services/dom.ts